import { useEffect } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';
import OfflineIndicator from './components/OfflineIndicator';
import InstallPrompt from './components/InstallPrompt';
import PushPermissionBanner from './components/PushPermissionBanner';
import { UpdatePrompt } from './components/UpdatePrompt';
import { registerSW } from './lib/registerSW';

import Login from './pages/Login';
import Register from './pages/Register';

import MobileLayout from './pages/mobile/MobileLayout';
import MapView from './pages/mobile/MapView';
import StationList from './pages/mobile/StationList';
import StationDetail from './pages/mobile/StationDetail';
import PriceEntry from './pages/mobile/PriceEntry';
import History from './pages/mobile/History';
import MobilePushes from './pages/mobile/MobilePushes';
import MobileDrafts from './pages/mobile/MobileDrafts';
import MobileRoute from './pages/mobile/MobileRoute';

import AdminLayout from './pages/admin/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminPrices from './pages/admin/AdminPrices';
import AdminPriceChanges from './pages/admin/AdminPriceChanges';
import AdminDepartments from './pages/admin/AdminDepartments';
import AdminStationPricing from './pages/admin/AdminStationPricing';
import AdminPushPrices from './pages/admin/AdminPushPrices';
import AdminStations from './pages/admin/AdminStations';
import AdminZones from './pages/admin/AdminZones';
import AdminProducts from './pages/admin/AdminProducts';
import AdminUsers from './pages/admin/AdminUsers';
import AdminTrackingRequests from './pages/admin/AdminTrackingRequests';
import AdminParameters from './pages/admin/AdminParameters';
import AdminWorkload from './pages/admin/AdminWorkload';
import AdminAuditLog from './pages/admin/AdminAuditLog';
import AdminMetrics from './pages/admin/AdminMetrics';
import AdminTrackingCompliance from './pages/admin/AdminTrackingCompliance';
import AdminDailyReport from './pages/admin/AdminDailyReport';
import AdminComms from './pages/admin/AdminComms';
import AdminMarkers from './pages/admin/AdminMarkers';
import AdminPriceMonitor from './pages/admin/AdminPriceMonitor';
import AdminNotificationCenter from './pages/admin/AdminNotificationCenter';
import AdminTradeAreas from './pages/admin/AdminTradeAreas';
import AdminApiClients from './pages/admin/AdminApiClients';
import AdminCurrentPrices from './pages/admin/AdminCurrentPrices';
import AdminHappyHourPage from './pages/admin/AdminHappyHourPage';
import TMLayout from './pages/tm/TMLayout';
import TMDashboard from './pages/tm/TMDashboard';

function ProtectedRoute({ children, role }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (role) {
    // "mobile" group: capturer, agent, territory_manager (TM entra por default al móvil; /tm sigue disponible manualmente)
    const mobileRoles = ['capturer', 'agent', 'territory_manager'];
    if (role === 'mobile' && !mobileRoles.includes(user.role)) return <Navigate to="/" replace />;
    if (role !== 'mobile' && user.role !== role) return <Navigate to="/" replace />;
  }
  return children;
}

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) {
    if (user.role === 'admin') return <Navigate to="/admin" replace />;
    return <Navigate to="/app" replace />;
  }
  return <Navigate to="/login" replace />;
}

function App() {
  useEffect(() => { document.title = 'PriceCapture · UNO'; registerSW(); }, []);
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />

            <Route path="/app" element={<ProtectedRoute role="mobile"><MobileLayout /></ProtectedRoute>}>
              <Route index element={<MobilePushes />} />
              <Route path="notifications" element={<Navigate to="/app/stations" replace />} />
              <Route path="route" element={<MobileRoute />} />
              <Route path="map" element={<MapView />} />
              <Route path="stations" element={<StationList />} />
              <Route path="station/:id" element={<StationDetail />} />
              <Route path="capture/:id" element={<PriceEntry />} />
              <Route path="history" element={<History />} />
              <Route path="pushes" element={<MobilePushes />} />
              <Route path="drafts" element={<MobileDrafts />} />
            </Route>

            <Route path="/admin" element={<ProtectedRoute role="admin"><AdminLayout /></ProtectedRoute>}>
              <Route index element={<AdminDashboard />} />
              <Route path="prices" element={<AdminPrices />} />
              <Route path="price-changes" element={<AdminPriceChanges />} />
              <Route path="tracking-requests" element={<AdminTrackingRequests />} />
              <Route path="push-prices" element={<AdminPushPrices />} />
              <Route path="stations" element={<AdminStations />} />
              <Route path="station-pricing" element={<AdminStationPricing />} />
              <Route path="departments" element={<AdminDepartments />} />
              <Route path="trade-areas" element={<AdminTradeAreas />} />
              <Route path="api-clients" element={<AdminApiClients />} />
              <Route path="zones" element={<AdminZones />} />
              <Route path="products" element={<AdminProducts />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="parameters" element={<AdminParameters />} />
              <Route path="workload" element={<AdminWorkload />} />
              <Route path="audit-log" element={<AdminAuditLog />} />
              <Route path="metrics" element={<AdminMetrics />} />
              <Route path="tracking-compliance" element={<AdminTrackingCompliance />} />
              <Route path="daily-report" element={<AdminDailyReport />} />
              <Route path="comms" element={<AdminComms />} />
              <Route path="markers" element={<AdminMarkers />} />
              <Route path="price-monitor" element={<AdminPriceMonitor />} />
              <Route path="current-prices" element={<AdminCurrentPrices />} />
              <Route path="happy-hour" element={<AdminHappyHourPage />} />
              <Route path="notification-center" element={<AdminNotificationCenter />} />
            </Route>

            <Route path="/tm" element={<ProtectedRoute role="territory_manager"><TMLayout /></ProtectedRoute>}>
              <Route index element={<TMDashboard />} />
            </Route>
          </Routes>
          <Toaster position="top-center" richColors />
          <OfflineIndicator />
          <InstallPrompt />
          <PushPermissionBanner />
          <UpdatePrompt />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
